import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MdSearch, 
  MdAdd, 
  MdFileDownload, 
  MdVisibility, 
  MdBlock, 
  MdCheckCircle,
  MdClose 
} from 'react-icons/md';
import './ClientsList.css';
import { getAllClientsForBroker, updateClientStatus } from '../../../lib/supabase/helpers';

export default function ClientsList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const data = await getAllClientsForBroker();
      console.log('Fetched clients:', data);
      const formattedClients = data.map(client => ({
        id: client.id,
        name: `${client.first_name} ${client.last_name}`,
        firstName: client.first_name,
        lastName: client.last_name,
        email: client.email || 'NOT_SUBMITTED',
        phone: client.phone || 'NOT_SUBMITTED',
        country: client.country || 'N/A',
        role: client.role.toUpperCase(),
        status: client.status?.toUpperCase() || 'ACTIVE',
        kycStatus: client.kyc_status?.toUpperCase() || 'PENDING',
        balance: client.wallets?.[0]?.total_balance || 0,
        mt5Login: client.mt5_accounts?.length > 0 
          ? client.mt5_accounts
          : 'NOT_SUBMITTED',
        createdAt: new Date(client.created_at),
        updatedAt: client.updated_at ? new Date(client.updated_at) : new Date(client.created_at)
      }));

      setClients(formattedClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async (clientId) => {
    if (window.confirm('Are you sure you want to block this client?')) {
      try {
        await updateClientStatus(clientId, 'blocked');
        await fetchClients();
        alert('Client blocked successfully!');
      } catch (error) {
        console.error('Error blocking client:', error);
        alert('Failed to block client');
      }
    }
  };

  const handleUnblock = async (clientId) => {
    try {
      await updateClientStatus(clientId, 'active');
      await fetchClients();
      alert('Client unblocked successfully!');
    } catch (error) {
      console.error('Error unblocking client:', error);
      alert('Failed to unblock client');
    }
  };

  const handleViewDetails = (client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Country', 'Status', 'KYC Status', 'Balance', 'MT5 Accounts', 'Joined Date'];
    const rows = filteredClients.map(client => [
      client.name,
      client.email,
      client.phone,
      client.country,
      client.status,
      client.kycStatus,
      client.balance,
      client.mt5Login !== "NOT_SUBMITTED" && client.mt5Login.length > 0 
        ? client.mt5Login.map(acc => acc.login_id).join('; ')
        : 'NOT_SUBMITTED',
      client.createdAt.toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'ALL' || client.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'ACTIVE').length,
    blocked: clients.filter(c => c.status === 'BLOCKED').length,
    kycApproved: clients.filter(c => c.kycStatus === 'APPROVED').length
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading clients...</p>
      </div>
    );
  }

  return (
    <div className="clients-container">
      {/* Stats Cards Row */}
      <div className="stats-cards-row">
        <div className="stat-card-item">
          <div className="stat-card-icon">👥</div>
          <div className="stat-card-info">
            <p className="stat-card-label">Total Clients</p>
            <h3 className="stat-card-value">{stats.total}</h3>
          </div>
        </div>
        <div className="stat-card-item">
          <div className="stat-card-icon">✅</div>
          <div className="stat-card-info">
            <p className="stat-card-label">Active</p>
            <h3 className="stat-card-value">{stats.active}</h3>
          </div>
        </div>
        <div className="stat-card-item">
          <div className="stat-card-icon">🔒</div>
          <div className="stat-card-info">
            <p className="stat-card-label">Blocked</p>
            <h3 className="stat-card-value">{stats.blocked}</h3>
          </div>
        </div>
        <div className="stat-card-item">
          <div className="stat-card-icon">✔️</div>
          <div className="stat-card-info">
            <p className="stat-card-label">KYC Approved</p>
            <h3 className="stat-card-value">{stats.kycApproved}</h3>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="clients-header">
        <div>
          <h1>Clients Management</h1>
          <p>Manage all your trading clients</p>
        </div>
        <button className="btn btn-primary">
          <MdAdd size={20} />
          <span>Add Client</span>
        </button>
      </div>

      {/* Filters */}
      <div className="clients-filters">
        <div className="search-box">
          <MdSearch size={20} />
          <input
            type="text"
            placeholder="Search by name, email, phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="BLOCKED">Blocked</option>
          <option value="PENDING">Pending</option>
        </select>

        <button className="btn btn-success" onClick={handleExportCSV}>
          <MdFileDownload size={20} />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Results Count */}
      <div className="results-info">
        <p>Showing {filteredClients.length} of {clients.length} clients</p>
      </div>

      {/* Clients Table - SCROLLABLE */}
      <div className="clients-table-wrapper">
        <div className="clients-table-container">
          <table className="clients-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Contact</th>
                <th>MT5 Login</th>
                <th>Balance</th>
                <th>KYC Status</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    No clients found
                  </td>
                </tr>
              ) : (
                filteredClients.map((client) => (
                  <tr key={client.id}>
                    <td>
                      <div className="client-info">
                        <div className="client-avatar">
                          {client.name.charAt(0)}
                        </div>
                        <div>
                          <div className="client-name">{client.name}</div>
                          <div className="client-email">{client.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{client.phone}</td>
                    <td>
                      <div className="mt5-badges-wrapper">
                        {client.mt5Login !== "NOT_SUBMITTED" && client.mt5Login.length > 0 ? (
                          client.mt5Login.map(login => (
                            <span key={login.id} className="mt5-badge">{login.login_id}</span>
                          ))
                        ) : (
                          <span className="mt5-badge">NOT_SUBMITTED</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="balance">${client.balance.toLocaleString()}</span>
                    </td>
                    <td>
                      <span className={`status-badge kyc-${client.kycStatus.toLowerCase()}`}>
                        {client.kycStatus}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${client.status.toLowerCase()}`}>
                        {client.status}
                      </span>
                    </td>
                    <td>{client.createdAt.toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn-icon btn-view" 
                          title="View Details"
                          onClick={() => handleViewDetails(client)}
                        >
                          <MdVisibility size={18} />
                        </button>
                        {client.status === 'ACTIVE' || client.status === 'SUSPENDED' ? (
                          <button 
                            className="btn-icon btn-block" 
                            title="Block Client"
                            onClick={() => handleBlock(client.id)}
                          >
                            <MdBlock size={18} />
                          </button>
                        ) : (
                          <button 
                            className="btn-icon btn-unblock" 
                            title="Unblock Client"
                            onClick={() => handleUnblock(client.id)}
                          >
                            <MdCheckCircle size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button className="btn btn-secondary" disabled>Previous</button>
        <span>Page 1 of 1</span>
        <button className="btn btn-secondary" disabled>Next</button>
      </div>

      {/* Client Details Modal */}
      {showDetailsModal && selectedClient && (
        <div className="client-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="client-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="client-modal-header">
              <h2>Client Details</h2>
              <button className="client-modal-close" onClick={() => setShowDetailsModal(false)}>
                <MdClose size={24} />
              </button>
            </div>
            
            <div className="client-modal-body">
              {/* Personal Information */}
              <div className="client-detail-section">
                <h3>Personal Information</h3>
                <div className="client-detail-grid">
                  <div className="client-detail-item">
                    <span className="client-detail-label">Full Name:</span>
                    <span className="client-detail-value">{selectedClient.name}</span>
                  </div>
                  <div className="client-detail-item">
                    <span className="client-detail-label">Email:</span>
                    <span className="client-detail-value">{selectedClient.email}</span>
                  </div>
                  <div className="client-detail-item">
                    <span className="client-detail-label">Phone:</span>
                    <span className="client-detail-value">{selectedClient.phone}</span>
                  </div>
                  <div className="client-detail-item">
                    <span className="client-detail-label">Country:</span>
                    <span className="client-detail-value">{selectedClient.country}</span>
                  </div>
                </div>
              </div>

              {/* Account Information */}
              <div className="client-detail-section">
                <h3>Account Information</h3>
                <div className="client-detail-grid">
                  <div className="client-detail-item">
                    <span className="client-detail-label">Account Status:</span>
                    <span className={`status-badge status-${selectedClient.status.toLowerCase()}`}>
                      {selectedClient.status}
                    </span>
                  </div>
                  <div className="client-detail-item">
                    <span className="client-detail-label">KYC Status:</span>
                    <span className={`status-badge kyc-${selectedClient.kycStatus.toLowerCase()}`}>
                      {selectedClient.kycStatus}
                    </span>
                  </div>
                  <div className="client-detail-item">
                    <span className="client-detail-label">Balance:</span>
                    <span className="balance">${selectedClient.balance.toLocaleString()}</span>
                  </div>
                  <div className="client-detail-item">
                    <span className="client-detail-label">Role:</span>
                    <span className="client-detail-value">{selectedClient.role}</span>
                  </div>
                  <div className="client-detail-item">
                    <span className="client-detail-label">Joined:</span>
                    <span className="client-detail-value">{selectedClient.createdAt.toLocaleDateString()}</span>
                  </div>
                  <div className="client-detail-item">
                    <span className="client-detail-label">Last Updated:</span>
                    <span className="client-detail-value">{selectedClient.updatedAt.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              {/* MT5 Accounts */}
              <div className="client-detail-section">
                <h3>MT5 Trading Accounts</h3>
                {selectedClient.mt5Login !== "NOT_SUBMITTED" && selectedClient.mt5Login.length > 0 ? (
                  <div className="mt5-accounts-list">
                    {selectedClient.mt5Login.map((account, index) => (
                      <div key={account.id} className="mt5-account-item">
                        <div className="mt5-account-header-item">
                          <span className="mt5-account-title">Account #{index + 1}</span>
                          <span className={`mt5-status-badge ${account.status || 'active'}`}>
                            {account.status || 'Active'}
                          </span>
                        </div>
                        <div className="mt5-account-details">
                          <div className="mt5-detail-row">
                            <span>Login ID:</span>
                            <strong>{account.login_id}</strong>
                          </div>
                          <div className="mt5-detail-row">
                            <span>Account Type:</span>
                            <strong>{account.account_type || 'Standard'}</strong>
                          </div>
                          <div className="mt5-detail-row">
                            <span>Server:</span>
                            <strong>{account.server || 'N/A'}</strong>
                          </div>
                          <div className="mt5-detail-row">
                            <span>Created:</span>
                            <strong>{account.created_at ? new Date(account.created_at).toLocaleDateString() : 'N/A'}</strong>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-mt5-message">
                    <p>No MT5 accounts created yet</p>
                  </div>
                )}
              </div>
            </div>

            <div className="client-modal-footer">
              {selectedClient.status === 'ACTIVE' || selectedClient.status === 'SUSPENDED' ? (
                <button 
                  className="btn btn-danger"
                  onClick={() => {
                    handleBlock(selectedClient.id);
                    setShowDetailsModal(false);
                  }}
                >
                  <MdBlock size={20} />
                  Block Client
                </button>
              ) : selectedClient.status === 'BLOCKED' ? (
                <button 
                  className="btn btn-success"
                  onClick={() => {
                    handleUnblock(selectedClient.id);
                    setShowDetailsModal(false);
                  }}
                >
                  <MdCheckCircle size={20} />
                  Unblock Client
                </button>
              ) : null}
              <button 
                className="btn btn-secondary"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}